import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Alert, Button, Card, DatePicker, Form, Modal, Radio, Select, Spin, message, theme, Typography } from 'antd'
import { ArrowLeftOutlined, DeleteOutlined, PrinterOutlined, StopOutlined, SwapOutlined, UndoOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import type { WorkOrder } from '@/types'
import { usePageTitle } from '@/contexts/PageTitleContext'
import { useWorkOrder, useDeleteWorkOrder } from '@/hooks/useWorkOrders'
import { usePermission } from '@/hooks/usePermission'
import { useWOStatusTypesList } from '@/hooks/useSystemTables'
import VoiceTextarea from '@/components/VoiceTextarea'
import { tornaIndietro } from '@/lib/navigazione'
import BarraStato from '@/components/scheda-v3/BarraStato'
import ElencoInterventi from '@/components/scheda-v3/ElencoInterventi'
import RigheRiepilogo from '@/components/scheda-v3/RigheRiepilogo'
import { tonoScheda, useColoriTono } from '@/components/scheda-v3/colori'
import { PhotoCaptureManager, PhotoCaptureManagerHandle } from '@/components/PhotoCaptureManager'
import AccettazioneStrip from '@/components/scheda-v2/AccettazioneStrip'
import axiosInstance from '@/lib/axios'
import { useRdasForWorkOrder } from '@/hooks/useRdaIntervento'
import { printWorkOrder } from '@/components/WorkOrderPrintView'
import type { PrintPhoto } from '@/components/WorkOrderPrintView'
import { toAxiosPrintPath, resizeImageForPrint } from '@/components/shared/printHelpers'
import type { PrintOption } from '@/components/shared/printHelpers'
import dayjs from 'dayjs'

const { Title } = Typography

const SchedaLavoroV3: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const workOrderId = Number(id)
  const navigate = useNavigate()
  const { token } = theme.useToken()
  const { setTitle } = usePageTitle()

  const { data: workOrder, isLoading, error, refetch } = useWorkOrder(workOrderId)
  const { data: statusTypes = [] } = useWOStatusTypesList()
  const queryClient = useQueryClient()

  const canDelete = usePermission('work_orders.delete')
  const deleteWO = useDeleteWorkOrder()

  const canEditWO = usePermission('work_orders.edit')

  // ── Annullamento / ripristino (W53) ─────────────────────────────────────────
  // NON e' una transizione di workflow: nel database non esiste nessuna
  // transizione verso 'annullata'. L'autorizzazione e' il permesso RBAC
  // work_orders.cancel, e gli endpoint sono /cancel e /restore.
  const canCancel = usePermission('work_orders.cancel')
  const [annullaModalOpen, setAnnullaModalOpen] = useState(false)
  const [annullaLoading, setAnnullaLoading] = useState(false)
  const [annullaHover, setAnnullaHover] = useState(false)
  const [ripristinaLoading, setRipristinaLoading] = useState(false)
  const [annullatoDa, setAnnullatoDa] = useState<string | null>(null)
  const [annullaForm] = Form.useForm()

  // Confronto via String(): l'enum WorkOrderStatus in types/index.ts non elenca
  // 'annullata' (ne' gli altri stati veri, che vivono in work_order_status_types),
  // e il confronto diretto non compilerebbe. Allargare l'enum e' un lavoro a se'.
  const isAnnullata = String(workOrder?.stato ?? '') === 'annullata'

  // Chi ha annullato non sta nella scheda: sta nell'audit trail, dove la
  // transizione e' registrata con il nome di chi l'ha eseguita. Si legge solo
  // quando serve, cioe' a scheda annullata.
  useEffect(() => {
    if (!isAnnullata) { setAnnullatoDa(null); return }
    axiosInstance.get(`/work-orders/${workOrderId}/audit-trail?limit=50`)
      .then(res => {
        const riga = (res.data?.audit_trail || []).find((e: any) => e.to_state === 'annullata')
        setAnnullatoDa(riga?.executor_name ?? null)
      })
      .catch(() => setAnnullatoDa(null))
  }, [isAnnullata, workOrderId])

  const [users, setUsers] = useState<{ value: number; label: string }[]>([])

  useEffect(() => {
    if (!canEditWO) return
    axiosInstance.get('/users/assegnabili').then(res => {
      const list: any[] = res.data.items || res.data || []
      setUsers(list.map((u: any) => ({
        value: Number(u.id),
        label: `${u.nome || ''} ${u.cognome || ''}`.trim() || u.email,
      })))
    }).catch(() => setUsers([]))
  }, [canEditWO])

  // ── Stampa PDF ────────────────────────────────────────────────────────────────
  // Stesso percorso di SchedaLavoroV2 (righe 105-161, 545-574, 797-831):
  // fetch parallelo print-data + photos → se foto presenti, modale con 3 opzioni.
  const { data: workOrderRdas = [] } = useRdasForWorkOrder(workOrderId)
  const [printFetching, setPrintFetching] = useState(false)
  const [printModalOpen, setPrintModalOpen] = useState(false)
  const [printLoading, setPrintLoading] = useState(false)
  const [printPhotos, setPrintPhotos] = useState<any[]>([])
  const [printDataCache, setPrintDataCache] = useState<any[]>([])
  const [printOption, setPrintOption] = useState<PrintOption>('none')

  const handlePrintConfirm = useCallback(async () => {
    if (!workOrder) return
    setPrintLoading(true)
    try {
      let photosToDownload: any[] = []
      if (printOption === 'accettazione') {
        photosToDownload = printPhotos.filter((p: any) => p.fase === 'accettazione')
      } else if (printOption === 'all') {
        photosToDownload = printPhotos
      }
      const results = await Promise.allSettled(
        photosToDownload.map(async (photo: any): Promise<PrintPhoto> => {
          const path = toAxiosPrintPath(photo.google_drive_url ?? '')
          const res = await axiosInstance.get<Blob>(path, { responseType: 'blob' })
          const ct: string = (res.headers['content-type'] as string) ?? ''
          if (!ct.startsWith('image/')) throw new Error(`Non è un'immagine: ${ct}`)
          const dataUrl = await resizeImageForPrint(res.data)
          return {
            id: photo.id,
            fase: photo.fase,
            file_name: photo.file_name ?? '',
            data_url: dataUrl,
            created_at: photo.created_at,
          }
        })
      )
      const printPhotosData: PrintPhoto[] = results
        .filter((r): r is PromiseFulfilledResult<PrintPhoto> => r.status === 'fulfilled')
        .map(r => r.value)
      printWorkOrder(workOrder, workOrderRdas, printDataCache, printPhotosData)
      setPrintModalOpen(false)
    } catch (err) {
      console.error('Errore preparazione foto per stampa:', err)
    } finally {
      setPrintLoading(false)
    }
  }, [workOrder, workOrderRdas, printOption, printPhotos, printDataCache])

  // Colori tono — hook chiamato sempre, prima dei guard, con optional chaining
  const colori = useColoriTono(tonoScheda(workOrder?.stato))

  const handleStateChange = useCallback(() => {
    refetch()
  }, [refetch])

  // ── Aggiornamento cache dopo salvataggio accettazione ────────────────────────
  // Stesso pattern di SchedaLavoroV2 riga 286: patch ottimistico sulla cache
  // React Query per evitare un refetch completo.
  const handleWorkOrderUpdate = useCallback((patch: Partial<WorkOrder>) => {
    queryClient.setQueryData(['work-order', workOrderId], (old: any) => ({
      ...old,
      ...patch,
    }))
  }, [queryClient, workOrderId])

  const handleSaveWOField = useCallback(async (patch: Partial<WorkOrder>) => {
    try {
      await axiosInstance.put(`/work-orders/${workOrderId}`, patch)
      handleWorkOrderUpdate(patch)
    } catch {
      message.error('Salvataggio fallito')
    }
  }, [workOrderId, handleWorkOrderUpdate])

  // ── Acquisizione foto da card intervento ─────────────────────────────────────
  // Stesso pattern di SchedaLavoroV2 righe 186-221: stato + ref + callback.
  // Il modale vive qui; PhotoCaptureManager gestisce staging/webcam/upload.
  const captureRef = useRef<PhotoCaptureManagerHandle>(null)
  const [captureInterventionId, setCaptureInterventionId] = useState<number | null>(null)
  const [captureModalOpen, setCaptureModalOpen] = useState(false)
  const [captureCommitting, setCaptureCommitting] = useState(false)

  type FaseFoto = 'accettazione' | 'lavorazione' | 'riconsegna'
  const FASE_FOTO_LABELS: Record<FaseFoto, string> = {
    accettazione: 'Accettazione',
    lavorazione: 'Lavorazione',
    riconsegna: 'Riconsegna',
  }
  const [captureFase, setCaptureFase] = useState<FaseFoto>('lavorazione')

  const openInterventionCapture = useCallback((interventionId: number) => {
    setCaptureInterventionId(interventionId)
    setCaptureFase('lavorazione')
    setCaptureModalOpen(true)
  }, [])

  const handleCommitCapture = useCallback(async () => {
    try {
      setCaptureCommitting(true)
      await captureRef.current?.commitPhotos()
      setCaptureModalOpen(false)
      queryClient.invalidateQueries({ queryKey: ['work-order-photos', workOrderId] })
    } catch {
      // commitPhotos mostra già il messaggio di errore
    } finally {
      setCaptureCommitting(false)
    }
  }, [queryClient, workOrderId])

  const handleCancelCapture = useCallback(() => {
    captureRef.current?.discardChanges()
    setCaptureModalOpen(false)
  }, [])

  // Titolo pagina — stesso pattern di SchedaLavoroV2
  useEffect(() => {
    if (workOrder) setTitle(`Scheda ${workOrder.numero_scheda}`)
    return () => { setTitle('') }
  }, [workOrder])

  // ── LOADING ──────────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
        <Spin size="large" />
      </div>
    )
  }

  // ── ERROR ────────────────────────────────────────────────────────────────────
  if (error || !workOrder) {
    return (
      <Alert
        type="error"
        message="Scheda non trovata"
        description="Impossibile caricare la scheda di lavoro richiesta."
        action={
          <Button onClick={() => navigate('/schede-lavoro')}>Torna alla lista</Button>
        }
      />
    )
  }

  // ── VISTA ────────────────────────────────────────────────────────────────────
  // Prima lettera maiuscola solo per la visualizzazione — il valore `stato` non viene toccato
  const capitalize = (s: string) => s ? s.charAt(0).toUpperCase() + s.slice(1) : s

  // ── Elimina scheda — stesso calcolo della V2 (righe 248-252, 493-503) ────────
  // firstState = primo stato per ordine, esclusi trasversali (sospesa/annullata).
  const firstState = [...statusTypes]
    .filter(s => s.nome !== 'sospesa' && s.nome !== 'annullata')
    .sort((a, b) => a.ordine - b.ordine)[0]
  const isDeletable = firstState ? workOrder.stato === firstState.nome : false

  // Motivo dell'annullamento, se c'e'. Le schede annullate prima del 03/09/2026
  // hanno commento_annullamento vuoto (o solo spazi): in quel caso la fascia
  // mostra la data e basta — niente trattino, niente <em> vuoto.
  const motivoAnnullamento =
    (workOrder.commento_annullamento || workOrder.commento_stato || '').trim()

  // Payload: { commento_stato } — lo stesso che manda la V2 (SchedaLavoroV2.tsx:264).
  // A differenza della V2 la motivazione e' SEMPRE obbligatoria, non solo quando
  // lo stato 'annullata' ha richiede_commento: una scheda annullata senza sapere
  // perche' e' un buco nello storico.
  const handleAnnullaSubmit = async (values: { commento_stato: string }) => {
    try {
      setAnnullaLoading(true)
      await axiosInstance.post(`/work-orders/${workOrderId}/cancel`, {
        commento_stato: values.commento_stato,
      })
      message.success('Scheda annullata')
      setAnnullaModalOpen(false)
      annullaForm.resetFields()
      // Si resta sulla pagina: la fascia mostra cosa e' successo e da li' si
      // ripristina. La V2 invece esce verso la lista.
      queryClient.invalidateQueries({ queryKey: ['work-order', workOrderId] })
      refetch()
    } catch (e: any) {
      message.error(e?.response?.data?.detail || "Errore durante l'annullamento")
    } finally {
      setAnnullaLoading(false)
    }
  }

  const handleRipristina = async () => {
    try {
      setRipristinaLoading(true)
      const res = await axiosInstance.post(`/work-orders/${workOrderId}/restore`)
      message.success(`Scheda ripristinata (stato: ${res.data?.restored_stato})`)
      queryClient.invalidateQueries({ queryKey: ['work-order', workOrderId] })
      refetch()
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Errore durante il ripristino')
    } finally {
      setRipristinaLoading(false)
    }
  }

  const handleDelete = () => {
    Modal.confirm({
      title: 'Elimina scheda',
      content: `Eliminare definitivamente la scheda N. ${workOrder.numero_scheda}? L'operazione non è reversibile.`,
      okText: 'Elimina',
      okType: 'danger',
      cancelText: 'Annulla',
      onOk: () => deleteWO.mutate(workOrderId, {
        // `replace` come nella pagina classica: la scheda non esiste piu', e la
        // sua voce di cronologia riaprirebbe una pagina morta.
        onSuccess: () => navigate('/schede-lavoro', { replace: true }),
      }),
    })
  }

  return (
    <div style={{ padding: 24, maxWidth: 800, margin: '0 auto' }}>
      {/* Indietro (al punto di partenza) + passaggio alla pagina classica.
          Sono due azioni diverse e si distinguono anche dall'icona: la freccia
          torna da dove si e' arrivati, lo scambio cambia pagina sulla stessa
          scheda. */}
      <div style={{ marginBottom: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => tornaIndietro(navigate)}
        >
          Indietro
        </Button>
        <Button
          icon={<SwapOutlined />}
          // `replace`: lo scambio fra le due versioni SOSTITUISCE la voce di
          // cronologia invece di aggiungerne una. Senza, dopo un paio di
          // passaggi avanti e indietro il pulsante Indietro riporterebbe alla
          // versione da cui si e' appena usciti, invece che al punto di partenza.
          onClick={() => navigate(`/schede-lavoro/${id}/classica`, { replace: true })}
        >
          Versione classica
        </Button>
      </div>

      {/* ── Fascia scheda annullata ────────────────────────────────────────── */}
      {isAnnullata && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={
            <span>
              Scheda annullata
              {workOrder.data_annullamento && (
                <> il <strong>{dayjs(workOrder.data_annullamento).format('DD/MM/YYYY HH:mm')}</strong></>
              )}
              {annullatoDa && <> da <strong>{annullatoDa}</strong></>}
              {motivoAnnullamento && (
                <> — <em>{motivoAnnullamento}</em></>
              )}
            </span>
          }
          description={
            workOrder.data_ripristino ? (
              <span style={{ fontSize: 12 }}>
                Era gia' stata ripristinata il{' '}
                <strong>{dayjs(workOrder.data_ripristino).format('DD/MM/YYYY HH:mm')}</strong>.
              </span>
            ) : undefined
          }
          action={
            canCancel ? (
              <Button
                type="primary"
                icon={<UndoOutlined />}
                loading={ripristinaLoading}
                onClick={handleRipristina}
              >
                Ripristina scheda
              </Button>
            ) : undefined
          }
        />
      )}

      {/* Il resto della pagina resta leggibile ma attenuato: si vede cosa si sta
          per ripristinare, senza che sembri una scheda viva. */}
      <div style={{ opacity: isAnnullata ? 0.55 : 1 }}>

      {/* ── Testata scheda ─────────────────────────────────────────────────── */}
      <Card
        style={{
          background: token.colorBgContainer,
          borderColor: token.colorBorder,
          borderTop: `3px solid ${colori.testo}`,
          marginBottom: 16,
        }}
      >
        {/* Riga alta: titolo + badge stato + elimina (se applicabile) */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={4} style={{ margin: 0, color: token.colorText }}>
            Scheda {workOrder.numero_scheda}
          </Title>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Button
              size="small"
              icon={<PrinterOutlined />}
              loading={printFetching}
              onClick={async () => {
                if (!workOrder) return
                setPrintFetching(true)
                let printData: any[] = []
                let fetchedPhotos: any[] = []
                try {
                  const [pdRes, photosRes] = await Promise.allSettled([
                    axiosInstance.get(`/purchase-requests/work-order/${workOrderId}/print-data`),
                    axiosInstance.get(`/work-orders/${workOrderId}/photos`),
                  ])
                  if (pdRes.status === 'fulfilled') printData = pdRes.value.data
                  if (photosRes.status === 'fulfilled') fetchedPhotos = photosRes.value.data
                } finally {
                  setPrintFetching(false)
                }
                if (fetchedPhotos.length === 0) {
                  printWorkOrder(workOrder, workOrderRdas, printData)
                } else {
                  setPrintPhotos(fetchedPhotos)
                  setPrintDataCache(printData)
                  setPrintOption('none')
                  setPrintModalOpen(true)
                }
              }}
            >
              Stampa PDF
            </Button>
            {canCancel && !isAnnullata && (
              <Button
                size="small"
                icon={<StopOutlined />}
                onMouseEnter={() => setAnnullaHover(true)}
                onMouseLeave={() => setAnnullaHover(false)}
                onFocus={() => setAnnullaHover(true)}
                onBlur={() => setAnnullaHover(false)}
                onClick={() => { annullaForm.resetFields(); setAnnullaModalOpen(true) }}
                // Secondario a riposo, pericoloso al passaggio del mouse: e'
                // un'azione distruttiva ma rara — chi la cerca la trova, chi non
                // la cerca non ci inciampa.
                style={annullaHover
                  ? { color: token.colorError, borderColor: token.colorError }
                  : undefined}
              >
                Annulla scheda
              </Button>
            )}
            {isDeletable && canDelete && (
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={handleDelete}
              >
                Elimina scheda
              </Button>
            )}
          <span style={{
            borderRadius: 999,
            padding: '0 12px',
            lineHeight: '24px',
            fontSize: 13,
            fontWeight: 600,
            color: colori.testo,
            background: colori.sfondo,
            border: `1px solid ${colori.bordo}`,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            whiteSpace: 'nowrap',
          }}>
            <span style={{
              width: 7,
              height: 7,
              borderRadius: '50%',
              background: 'currentColor',
              display: 'inline-block',
              flexShrink: 0,
            }} />
            {capitalize(workOrder.stato)}
          </span>
          </div>
        </div>
        {/* Riga analisi: data entrata veicolo + tecnico analisi */}
        <div style={{ marginTop: 12, display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, color: token.colorTextSecondary }}>Data entrata veicolo:</span>
            {canEditWO ? (
              <DatePicker
                showTime={{ format: 'HH:mm', defaultValue: dayjs('08:30', 'HH:mm') }}
                size="small"
                value={workOrder.data_entrata_veicolo ? dayjs(workOrder.data_entrata_veicolo) : null}
                onChange={(date) => {
                  if (date) {
                    const appuntamento = workOrder.data_appuntamento ? dayjs(workOrder.data_appuntamento) : null
                    const completamento = workOrder.data_completamento ? dayjs(workOrder.data_completamento) : null
                    if (appuntamento && date.isBefore(appuntamento, 'minute')) {
                      message.warning('La data entrata veicolo non può essere prima della data appuntamento')
                      return
                    }
                    if (completamento && !date.isBefore(completamento, 'minute')) {
                      message.warning('La data entrata veicolo deve essere prima della data completamento')
                      return
                    }
                  }
                  handleSaveWOField({ data_entrata_veicolo: date ? date.toISOString() : null })
                }}
                format="DD/MM/YYYY HH:mm"
                style={{ width: 170 }}
                placeholder="Seleziona data e ora"
              />
            ) : (
              <span style={{ fontSize: 13 }}>
                {workOrder.data_entrata_veicolo
                  ? dayjs(workOrder.data_entrata_veicolo).format('DD/MM/YYYY HH:mm')
                  : '—'}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, color: token.colorTextSecondary }}>Tecnico analisi:</span>
            {canEditWO ? (
              <Select
                size="small"
                allowClear
                placeholder="Seleziona tecnico..."
                style={{ width: 200 }}
                value={workOrder.tecnico_analisi_id ?? undefined}
                options={users}
                onChange={(val) => handleSaveWOField({ tecnico_analisi_id: val })}
              />
            ) : (
              <span style={{ fontSize: 13 }}>
                {`${workOrder.tecnico_analisi?.nome || ''} ${workOrder.tecnico_analisi?.cognome || ''}`.trim() || '—'}
              </span>
            )}
          </div>
        </div>
      </Card>

      {/* Strip accettazione — dati cliente/veicolo + pulsante Modifica */}
      <AccettazioneStrip workOrder={workOrder} onSaved={handleWorkOrderUpdate} style={{ margin: '0 0 12px' }} />

      <BarraStato
        workOrderId={workOrderId}
        currentStato={workOrder.stato}
        statusTypes={statusTypes}
        interventionsCount={workOrder.interventions?.length ?? 0}
        hasDescrizione={false}
        onStateChange={handleStateChange}
      />

      <ElencoInterventi
        workOrder={workOrder}
        onChange={handleStateChange}
        onPhotoCaptureIntervention={openInterventionCapture}
      />

      <RigheRiepilogo
        workOrderId={workOrderId}
        interventions={workOrder.interventions ?? []}
      />

      </div>

      {/* Modale acquisizione foto da card intervento */}
      <Modal
        title={
          <span>
            Foto intervento
            {captureInterventionId != null
              ? ` — intervento #${captureInterventionId}`
              : ''}
            {' '}
            <Select
              size="small"
              value={captureFase}
              onChange={(v) => setCaptureFase(v as FaseFoto)}
              style={{ marginLeft: 8, width: 140 }}
              options={Object.entries(FASE_FOTO_LABELS).map(([k, label]) => ({ value: k, label }))}
            />
          </span>
        }
        open={captureModalOpen}
        onCancel={handleCancelCapture}
        onOk={handleCommitCapture}
        confirmLoading={captureCommitting}
        okText="Salva foto"
        cancelText="Annulla"
        width={520}
        destroyOnClose
      >
        <PhotoCaptureManager
          ref={captureRef}
          workOrderId={workOrderId}
          fase={captureFase}
          interventionId={captureInterventionId}
          compact={false}
          readOnly={false}
        />
      </Modal>

      {/* Modale opzioni stampa foto */}
      <Modal
        title="Opzioni di stampa"
        open={printModalOpen}
        onCancel={() => { if (!printLoading) { setPrintModalOpen(false) } }}
        onOk={handlePrintConfirm}
        confirmLoading={printLoading}
        okText={printLoading ? 'Preparazione foto…' : 'Stampa'}
        cancelText="Annulla"
        cancelButtonProps={{ disabled: printLoading }}
        maskClosable={!printLoading}
        width={420}
      >
        <Radio.Group
          value={printOption}
          onChange={e => setPrintOption(e.target.value as PrintOption)}
          style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: '8px 0' }}
        >
          <Radio value="none">
            <strong>Senza foto</strong>
            <span style={{ color: token.colorTextTertiary, marginLeft: 6 }}>— stampa identica a oggi</span>
          </Radio>
          <Radio value="accettazione">
            Solo accettazione
            <span style={{ marginLeft: 8, color: token.colorPrimary, fontWeight: 600 }}>
              ({printPhotos.filter((p: any) => p.fase === 'accettazione').length} foto)
            </span>
          </Radio>
          <Radio value="all">
            Tutte le foto
            <span style={{ marginLeft: 8, color: token.colorPrimary, fontWeight: 600 }}>
              ({printPhotos.length} foto)
            </span>
          </Radio>
        </Radio.Group>
      </Modal>

      {/* ── Modale annullamento: motivazione obbligatoria ─────────────────── */}
      <Modal
        title="Commento annullamento"
        open={annullaModalOpen}
        onCancel={() => { setAnnullaModalOpen(false); annullaForm.resetFields() }}
        onOk={() => annullaForm.submit()}
        confirmLoading={annullaLoading}
        okText="Annulla scheda"
        okButtonProps={{ danger: true }}
        cancelText="Indietro"
      >
        <Alert
          type="warning"
          showIcon
          message={`La scheda N. ${workOrder.numero_scheda} verrà annullata`}
          description="I dati restano tutti: la scheda esce dalla lista principale e potrai ripristinarla da qui. Inserisci un commento obbligatorio per procedere."
          style={{ marginBottom: 16 }}
        />
        <Form form={annullaForm} onFinish={handleAnnullaSubmit}>
          <Form.Item
            name="commento_stato"
            rules={[
              { required: true, message: 'Inserisci un commento' },
              { min: 3, message: 'Almeno 3 caratteri' },
            ]}
          >
            <VoiceTextarea rows={4} minHeight={100} maxHeight={220} placeholder="Motivo annullamento..." label="Commento" compact />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default SchedaLavoroV3
